import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'environments/environment';
import Pusher from 'pusher-js';
import { NotificationsService } from 'app/layout/components/navbar/navbar-notification/notifications.service';

// Interface para notificação recebida via Pusher
export interface PushNotificationData {
  titulo: string;
  mensagem: string;
  url?: string;
  tipo: string;
  timestamp: string;
  dados_extras?: any;
}

// Interface para controle de estado das notificações
export interface NotificationState {
  isConnected: boolean;
  hasPermission: boolean;
  isSubscribed: boolean;
  lastError?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PushNotificationService {
  private pusher: Pusher | null = null;
  private channel: any = null;
  private userUuid: string | null = null;
  
  // BehaviorSubject para controlar estado das notificações
  private notificationStateSubject = new BehaviorSubject<NotificationState>({
    isConnected: false,
    hasPermission: false,
    isSubscribed: false
  });
  
  // Observable para componentes se inscreverem
  public notificationState$ = this.notificationStateSubject.asObservable();
  
  // Subject para notificações recebidas
  private notificationReceivedSubject = new BehaviorSubject<PushNotificationData | null>(null);
  public notificationReceived$ = this.notificationReceivedSubject.asObservable();

  // Flag para controlar se deve exibir alerta de novas notificações
  private shouldShowNotificationAlert: boolean = true;

  constructor(
    private router: Router,
    private notificationsService: NotificationsService
  ) {
    this.initializePusher();
  }

  /**
   * Inicializa o cliente Pusher
   */
  private initializePusher(): void {
    try {
      if (!environment.pusher?.key) {
        console.error('PushNotificationService: Pusher key não configurada no environment');
        this.updateState({ lastError: 'Pusher key não configurada' });
        return;
      }

      this.pusher = new Pusher(environment.pusher.key, {
        cluster: environment.pusher.cluster,
        forceTLS: environment.pusher.forceTLS,
        enabledTransports: ['ws', 'wss'],
        disabledTransports: []
      });

      // Event listeners para conexão
      this.pusher.connection.bind('connected', () => {
        console.log('PushNotificationService: Conectado ao Pusher');
        this.updateState({ isConnected: true });
      });

      this.pusher.connection.bind('disconnected', () => {
        console.log('PushNotificationService: Desconectado do Pusher');
        this.updateState({ isConnected: false });
      });

      this.pusher.connection.bind('error', (error: any) => {
        console.error('PushNotificationService: Erro de conexão Pusher:', error);
        this.updateState({ lastError: `Erro de conexão: ${error.message}` });
      });

    } catch (error) {
      console.error('PushNotificationService: Erro ao inicializar Pusher:', error);
      this.updateState({ lastError: `Erro de inicialização: ${error}` });
    }
  }

  /**
   * Solicita permissão para notificações do browser
   */
  public async requestPermission(): Promise<boolean> {
    try {
      if (!('Notification' in window)) {
        console.warn('PushNotificationService: Notificações não suportadas neste browser');
        this.updateState({ hasPermission: false, lastError: 'Notificações não suportadas' });
        return false;
      }

      if (Notification.permission === 'granted') {
        this.updateState({ hasPermission: true });
        return true;
      }

      if (Notification.permission === 'denied') {
        console.warn('PushNotificationService: Permissão de notificação negada pelo usuário');
        this.updateState({ hasPermission: false, lastError: 'Permissão negada pelo usuário' });
        return false;
      }

      // Solicita permissão
      const permission = await Notification.requestPermission();
      const hasPermission = permission === 'granted';
      
      this.updateState({ 
        hasPermission,
        lastError: hasPermission ? undefined : 'Permissão de notificação negada'
      });

      console.log(`PushNotificationService: Permissão de notificação: ${permission}`);
      return hasPermission;

    } catch (error) {
      console.error('PushNotificationService: Erro ao solicitar permissão:', error);
      this.updateState({ hasPermission: false, lastError: `Erro ao solicitar permissão: ${error}` });
      return false;
    }
  }

  /**
   * Subscreve às notificações para um usuário específico
   */
  public subscribeToNotifications(userUuid: string): void {
    try {
      if (!this.pusher) {
        console.error('PushNotificationService: Pusher não inicializado');
        this.updateState({ lastError: 'Pusher não inicializado' });
        return;
      }

      if (!userUuid) {
        console.error('PushNotificationService: userUuid é obrigatório');
        this.updateState({ lastError: 'userUuid é obrigatório' });
        return;
      }

      // Desinscreve do canal anterior se existir
      this.unsubscribeFromNotifications();

      this.userUuid = userUuid;
      const channelName = `backoffice-user-${userUuid}`;
      
      console.log(`PushNotificationService: Subscrevendo ao canal: ${channelName}`);
      
      // Subscreve ao canal
      this.channel = this.pusher.subscribe(channelName);

      // Event listeners do canal
      this.channel.bind('pusher:subscription_succeeded', () => {
        console.log(`🎉 PushNotificationService: Subscrito com sucesso ao canal: ${channelName}`);
        console.log(`🔍 Canal ativo: ${this.channel.name}`);
        console.log(`🔍 Estado da conexão Pusher: ${this.pusher.connection.state}`);
        this.updateState({ isSubscribed: true });
      });

      this.channel.bind('pusher:subscription_error', (error: any) => {
        console.error(`❌ PushNotificationService: Erro ao subscrever ao canal: ${channelName}`, error);
        this.updateState({ isSubscribed: false, lastError: `Erro de subscrição: ${error.message}` });
      });

      // Listener para o evento de limite de compras
      this.channel.bind('limite-compras-notification', (data: PushNotificationData) => {
        console.log('🔔 PushNotificationService: Notificação recebida:', data);
        this.handleNotificationReceived(data);
      });

      // Listener genérico para debug - captura TODOS os eventos
      this.channel.bind_global((eventName: string, data: any) => {
        console.log(`🔍 DEBUG: Evento recebido no canal ${channelName}:`, eventName, data);
      });

    } catch (error) {
      console.error('PushNotificationService: Erro ao subscrever às notificações:', error);
      this.updateState({ isSubscribed: false, lastError: `Erro de subscrição: ${error}` });
    }
  }

  /**
   * Desinscreve das notificações
   */
  public unsubscribeFromNotifications(): void {
    try {
      if (this.channel && this.userUuid) {
        const channelName = `backoffice-user-${this.userUuid}`;
        console.log(`PushNotificationService: Desinscrevendo do canal: ${channelName}`);
        
        this.pusher?.unsubscribe(channelName);
        this.channel = null;
        this.userUuid = null;
        
        this.updateState({ isSubscribed: false });
      }
    } catch (error) {
      console.error('PushNotificationService: Erro ao desinscrever das notificações:', error);
    }
  }

  /**
   * Processa notificação recebida
   */
  private handleNotificationReceived(data: PushNotificationData): void {
    try {
      console.log('🔔 Processando notificação:', data);
      
      // Emite a notificação para componentes interessados
      this.notificationReceivedSubject.next(data);

      // Exibe notificação nativa do browser (se permitido)
      this.showBrowserNotification(data);

      // Para notificações de limite de compras, apenas atualiza o contador do sino
      // sem exibir o alerta de "Novas Notificações"
      if (this.isLimiteComprasNotification(data)) {
        console.log('📊 Notificação de limite de compras - atualizando apenas contador');
        this.updateNotificationCounterSilently(data);
      } else {
        console.log('🔔 Notificação normal - permitindo alerta padrão');
        // Para outros tipos de notificação, permite o comportamento normal
        this.addToNotificationSystem(data);
      }

    } catch (error) {
      console.error('PushNotificationService: Erro ao processar notificação:', error);
    }
  }

  /**
   * Exibe notificação nativa do browser
   */
  private showBrowserNotification(data: PushNotificationData): void {
    try {
      if (!('Notification' in window) || Notification.permission !== 'granted') {
        return;
      }

      const notification = new Notification(data.titulo, {
        body: data.mensagem,
        icon: '/assets/images/logo/favicon.png', // Ajuste o caminho conforme necessário
        badge: '/assets/images/logo/favicon.png',
        tag: `buyhelp-${data.tipo}-${Date.now()}`,
        requireInteraction: true,
        silent: false
      });

      // Clique na notificação nativa
      notification.onclick = () => {
        window.focus();
        if (data.url) {
          this.navigateToUrl(data.url);
        }
        notification.close();
      };

      // Auto-close após 10 segundos
      setTimeout(() => {
        notification.close();
      }, 10000);

    } catch (error) {
      console.error('PushNotificationService: Erro ao exibir notificação do browser:', error);
    }
  }

  /**
   * Navega para URL da notificação
   */
  public navigateToUrl(url: string): void {
    try {
      if (!url) return;

      console.log(`PushNotificationService: Navegando para: ${url}`);
      
      // Remove leading slash se presente para Angular Router
      const routeUrl = url.startsWith('/') ? url.substring(1) : url;
      
      // Usa Angular Router para navegação suave
      this.router.navigateByUrl(routeUrl).then(success => {
        if (!success) {
          console.warn('PushNotificationService: Falha na navegação via Router, usando fallback');
          window.location.href = url;
        }
      }).catch(error => {
        console.error('PushNotificationService: Erro na navegação via Router:', error);
        window.location.href = url;
      });

    } catch (error) {
      console.error('PushNotificationService: Erro ao navegar para URL:', error);
      window.location.href = url;
    }
  }

  /**
   * Atualiza o estado das notificações
   */
  private updateState(updates: Partial<NotificationState>): void {
    const currentState = this.notificationStateSubject.value;
    const newState = { ...currentState, ...updates };
    this.notificationStateSubject.next(newState);
  }

  /**
   * Obtém o estado atual das notificações
   */
  public getCurrentState(): NotificationState {
    return this.notificationStateSubject.value;
  }

  /**
   * Verifica se deve exibir alerta de novas notificações
   */
  public shouldShowAlert(): boolean {
    return this.shouldShowNotificationAlert;
  }

  /**
   * Retorna o canal atual ao qual o serviço está inscrito
   */
  public getCurrentChannel(): string | null {
    return this.channel ? this.channel.name : null;
  }

  /**
   * Reconecta ao Pusher (útil para retry em caso de erro)
   */
  public reconnect(): void {
    try {
      console.log('PushNotificationService: Tentando reconectar...');
      
      if (this.pusher) {
        this.pusher.disconnect();
      }
      
      // Reinicializa após um pequeno delay
      setTimeout(() => {
        this.initializePusher();
        
        // Re-subscreve se havia um usuário conectado
        if (this.userUuid) {
          setTimeout(() => {
            this.subscribeToNotifications(this.userUuid!);
          }, 1000);
        }
      }, 1000);

    } catch (error) {
      console.error('PushNotificationService: Erro ao reconectar:', error);
      this.updateState({ lastError: `Erro de reconexão: ${error}` });
    }
  }

  /**
   * Função de teste para verificar conectividade e configuração
   */
  public testConnection(): void {
    console.log('🧪 === TESTE DE DIAGNÓSTICO PUSHER ===');
    console.log('🔍 Pusher inicializado:', !!this.pusher);
    console.log('🔍 Estado da conexão:', this.pusher?.connection?.state);
    console.log('🔍 Canal atual:', this.channel?.name);
    console.log('🔍 UserUuid atual:', this.userUuid);
    console.log('🔍 Configuração Pusher:', {
      key: environment.pusher.key,
      cluster: environment.pusher.cluster,
      forceTLS: environment.pusher.forceTLS
    });
    
    if (this.pusher && this.pusher.connection.state === 'connected') {
      console.log('✅ Conexão Pusher OK');
    } else {
      console.log('❌ Problema na conexão Pusher');
    }
    
    if (this.channel) {
      console.log('✅ Canal subscrito:', this.channel.name);
      console.log('🔍 Eventos bound no canal:', Object.keys(this.channel.callbacks || {}));
    } else {
      console.log('❌ Nenhum canal subscrito');
    }
    
    console.log('🧪 === FIM DO TESTE ===');
  }

  /**
   * Verifica se a notificação é do tipo limite de compras
   */
  private isLimiteComprasNotification(data: PushNotificationData): boolean {
    return data.tipo === 'limite-compras' || 
           data.titulo?.toLowerCase().includes('limite') ||
           data.mensagem?.toLowerCase().includes('limite de compras');
  }

  /**
   * Atualiza o contador de notificações silenciosamente (sem alerta)
   * para notificações de limite de compras
   */
  private updateNotificationCounterSilently(data: PushNotificationData): void {
    try {
      console.log('🔕 Atualizando contador silenciosamente para:', data.titulo);
      
      // Desabilita temporariamente o alerta
      this.shouldShowNotificationAlert = false;
      
      // Recarrega as notificações do backend para atualizar o contador
      // mas não dispara o alerta de "Novas Notificações"
      this.notificationsService.getNotificationsData().then(() => {
        console.log('✅ Contador de notificações atualizado silenciosamente');
        
        // Reabilita o alerta após um pequeno delay
        setTimeout(() => {
          this.shouldShowNotificationAlert = true;
        }, 1000);
        
      }).catch(error => {
        console.error('❌ Erro ao atualizar contador silenciosamente:', error);
        // Reabilita o alerta mesmo em caso de erro
        this.shouldShowNotificationAlert = true;
      });
      
    } catch (error) {
      console.error('PushNotificationService: Erro ao atualizar contador silenciosamente:', error);
      // Reabilita o alerta em caso de erro
      this.shouldShowNotificationAlert = true;
    }
  }

  /**
   * Adiciona notificação ao sistema normal (com alerta)
   * para outros tipos de notificação
   */
  private addToNotificationSystem(data: PushNotificationData): void {
    try {
      console.log('🔔 Adicionando notificação ao sistema normal:', data.titulo);
      
      // Para notificações normais, recarrega e permite o alerta
      this.notificationsService.getNotificationsData().then(() => {
        console.log('✅ Notificação adicionada ao sistema com alerta');
      }).catch(error => {
        console.error('❌ Erro ao adicionar notificação ao sistema:', error);
      });
      
    } catch (error) {
      console.error('PushNotificationService: Erro ao adicionar notificação ao sistema:', error);
    }
  }

  /**
   * Cleanup ao destruir o serviço
   */
  public ngOnDestroy(): void {
    this.unsubscribeFromNotifications();
    if (this.pusher) {
      this.pusher.disconnect();
    }
  }
}
